import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const SHOW_DELAY_MS = 300
const HIDE_DELAY_MS = 100

type SidebarTooltipProps = {
  label: string
  children: React.ReactElement
  enabled: boolean
  /** Center the anchor when sidebar is icon-only (64px rail). */
  alignCenter?: boolean
}

export function SidebarTooltip({ label, children, enabled, alignCenter = false }: SidebarTooltipProps) {
  const tooltipId = useId()
  const anchorRef = useRef<HTMLDivElement>(null)
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const clearTimers = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current)
      showTimer.current = null
    }
    if (hideTimer.current) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }, [])

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    setPosition({ top: rect.top + rect.height / 2, left: rect.right + 8 })
  }, [])

  const scheduleShow = useCallback(() => {
    clearTimers()
    showTimer.current = setTimeout(() => {
      updatePosition()
      setVisible(true)
    }, SHOW_DELAY_MS)
  }, [clearTimers, updatePosition])

  const scheduleHide = useCallback(() => {
    clearTimers()
    hideTimer.current = setTimeout(() => setVisible(false), HIDE_DELAY_MS)
  }, [clearTimers])

  useEffect(() => () => clearTimers(), [clearTimers])

  if (!enabled) {
    return children
  }

  const child = React.Children.only(children)

  return (
    <>
      <div
        ref={anchorRef}
        className={`relative ${alignCenter ? 'flex w-full justify-center' : 'w-full'}`}
        onMouseEnter={scheduleShow}
        onMouseLeave={scheduleHide}
      >
        {React.cloneElement(child, {
          'aria-describedby': visible ? tooltipId : undefined,
          onFocus: (event: React.FocusEvent) => {
            child.props.onFocus?.(event)
            scheduleShow()
          },
          onBlur: (event: React.FocusEvent) => {
            child.props.onBlur?.(event)
            scheduleHide()
          },
        })}
      </div>
      {visible &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            style={{ top: position.top, left: position.left }}
            className="pointer-events-none fixed z-[100] max-w-[200px] -translate-y-1/2 truncate rounded-md border border-border bg-surface-default px-2 py-1 text-caption text-text-primary shadow-sm"
          >
            {label}
          </div>,
          document.body
        )}
    </>
  )
}
